import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Zap, Bike, Crown, Gem, Check, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIERS = [
    {
        id: 'free',
        name: 'Free',
        price: '$0',
        cadence: 'forever',
        tagline: 'Get rolling',
        icon: Bike,
        accent: 'text-muted-foreground',
        ring: 'border-white/10',
        glow: '',
        features: [
            'Track rides & earn Bolt',
            'Browse the BTR marketplace',
            'Leaderboards & social feed',
            'Community access',
        ],
        available: true,
    },
    {
        id: 'standard',
        name: 'Standard',
        price: '$9.99',
        cadence: '/month',
        tagline: 'Most Popular',
        icon: Zap,
        accent: 'text-primary',
        ring: 'border-primary/40',
        glow: 'shadow-[0_0_40px_rgba(30,144,255,0.25)]',
        features: [
            '2× Bolt earn rate',
            'Premium challenges',
            'Priority leaderboard placement',
            'Monthly Bolt bonus',
            'Early access to new features',
        ],
        available: true,
        popular: true,
    },
    {
        id: 'pro',
        name: 'Pro',
        price: 'Soon',
        cadence: '',
        tagline: 'Coming Soon',
        icon: Crown,
        accent: 'text-boltz-gold',
        ring: 'border-white/10',
        glow: '',
        features: [
            'Everything in Standard',
            'Seller storefronts',
            'Advanced ride analytics',
            'Exclusive drops',
        ],
        available: false,
    },
    {
        id: 'elite',
        name: 'Elite',
        price: 'Soon',
        cadence: '',
        tagline: 'Coming Soon',
        icon: Gem,
        accent: 'text-hot-pink',
        ring: 'border-white/10',
        glow: '',
        features: [
            'Everything in Pro',
            'Governance voting power',
            'VIP events & rewards',
            'Concierge support',
        ],
        available: false,
    },
];

export default function TierSelector({ onDone }) {
    const [busy, setBusy] = useState('');

    const selectFree = async () => {
        setBusy('free');
        try {
            const r = await fetch(`${API}/subscription/update`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tier: 'free' }),
            });
            if (r.ok) { toast.success('Free plan activated — welcome to Boltorium!'); onDone(); }
            else { const e = await r.json(); toast.error(e.detail || 'Something went wrong'); }
        } catch (e) { toast.error('Network error'); }
        finally { setBusy(''); }
    };

    const selectStandard = async () => {
        setBusy('standard');
        try {
            const r = await fetch(`${API}/subscription/checkout`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tier: 'standard', origin_url: window.location.origin }),
            });
            const d = await r.json();
            if (r.ok && d.checkout_url) { window.location.href = d.checkout_url; }
            else { toast.error(d.detail || 'Could not start checkout'); setBusy(''); }
        } catch (e) { toast.error('Network error'); setBusy(''); }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-background" data-testid="tier-selector-screen">
            <div className="absolute inset-0 pointer-events-none opacity-40"
                style={{ background: 'radial-gradient(circle at 20% 0%, rgba(30,144,255,0.12), transparent 45%), radial-gradient(circle at 80% 100%, rgba(255,20,147,0.10), transparent 45%)' }} />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
                    <span className="label-text text-primary text-glow">CHOOSE YOUR RIDE PLAN</span>
                    <h1 className="text-3xl sm:text-5xl font-black mt-2 font-display text-foreground tracking-wide">
                        Power up your ride
                    </h1>
                    <p className="text-muted-foreground text-sm sm:text-base mt-3 max-w-xl mx-auto">
                        Pick a plan to enter Boltorium. Start free, or unlock 2× Bolt and premium perks with Standard.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                    {TIERS.map((tier, idx) => {
                        const Icon = tier.icon;
                        return (
                            <motion.div
                                key={tier.id}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.08 }}
                                className={`relative rounded-2xl border ${tier.ring} ${tier.glow} bg-white/[0.03] backdrop-blur-xl p-5 flex flex-col ${!tier.available ? 'opacity-60' : ''}`}
                                data-testid={`tier-card-${tier.id}`}
                            >
                                {tier.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-black text-[10px] font-display font-bold px-3 py-1 rounded-full tracking-wider">
                                        MOST POPULAR
                                    </div>
                                )}

                                <div className="flex items-center gap-3 mb-4">
                                    <div className={`w-11 h-11 rounded-xl bg-white/5 border ${tier.ring} flex items-center justify-center`}>
                                        <Icon className={`h-5 w-5 ${tier.accent}`} />
                                    </div>
                                    <div>
                                        <h3 className={`font-display font-bold text-lg ${tier.accent}`}>{tier.name}</h3>
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{tier.tagline}</p>
                                    </div>
                                </div>

                                <div className="mb-5 flex items-baseline gap-1">
                                    <span className="text-3xl font-black font-mono text-foreground">{tier.price}</span>
                                    {tier.cadence && <span className="text-xs text-muted-foreground">{tier.cadence}</span>}
                                </div>

                                <ul className="space-y-2.5 flex-1 mb-6">
                                    {tier.features.map(f => (
                                        <li key={f} className="flex items-start gap-2 text-xs">
                                            <Check className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${tier.available ? tier.accent : 'text-muted-foreground/40'}`} />
                                            <span className={tier.available ? 'text-foreground/90' : 'text-muted-foreground/50'}>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {tier.id === 'free' && (
                                    <Button onClick={selectFree} disabled={busy === 'free'}
                                        className="w-full rounded-full font-display bg-white/10 text-foreground hover:bg-white/20"
                                        data-testid="select-free-btn">
                                        {busy === 'free' ? 'Activating…' : 'Continue Free'}
                                    </Button>
                                )}

                                {tier.id === 'standard' && (
                                    <Button onClick={selectStandard} disabled={busy === 'standard'}
                                        className="w-full rounded-full font-display bg-primary text-black hover:bg-primary/80"
                                        data-testid="select-standard-btn">
                                        {busy === 'standard' ? 'Redirecting…' : 'Subscribe · $9.99/mo'}
                                    </Button>
                                )}

                                {!tier.available && (
                                    <Button disabled
                                        className="w-full rounded-full font-display bg-white/5 text-muted-foreground cursor-not-allowed"
                                        data-testid={`select-${tier.id}-btn`}>
                                        <Lock className="h-3.5 w-3.5 mr-1.5" /> Coming Soon
                                    </Button>
                                )}
                            </motion.div>
                        );
                    })}
                </div>

                <p className="text-center text-[11px] text-muted-foreground mt-8">
                    Standard is billed monthly via Stripe. Cancel anytime. Cards accepted worldwide.
                </p>
            </div>
        </div>
    );
}
